/**
 * Vue directive to prevent tab navigation into child elements.
 * Usage: v-no-tab-into
 *
 * This directive ensures that interactive elements within the element
 * cannot be reached via tab navigation, while still allowing programmatic focus.
 */

interface HTMLElementWithObserver extends HTMLElement {
  _noTabIntoObserver?: MutationObserver;
}

const SELECTORS =
  'button, input, select, textarea, a, [tabindex]:not([tabindex="-1"])';

function disableTabbing(el: HTMLElement) {
  const elements = el.querySelectorAll(SELECTORS);
  elements.forEach((element) => {
    const currentTabIndex = element.getAttribute('tabindex');
    if (currentTabIndex !== '-1') {
      // Store original value for potential restoration
      element.setAttribute('data-original-tabindex', currentTabIndex || '0');
      element.setAttribute('tabindex', '-1');
    }
  });
}

function setupMutationObserver(el: HTMLElement) {
  const observer = new MutationObserver(() => {
    // Re-apply when DOM changes
    disableTabbing(el);
  });

  observer.observe(el, {
    childList: true,
    subtree: true,
  });

  // Store observer on element for cleanup
  (el as HTMLElementWithObserver)._noTabIntoObserver = observer;
}

function cleanup(el: HTMLElement) {
  const elementWithObserver = el as HTMLElementWithObserver;
  const observer = elementWithObserver._noTabIntoObserver;
  if (observer) {
    observer.disconnect();
    delete elementWithObserver._noTabIntoObserver;
  }
}

export const noTabInto = {
  mounted(el: HTMLElement) {
    disableTabbing(el);
    setupMutationObserver(el);
  },

  updated(el: HTMLElement) {
    disableTabbing(el);
  },

  unmounted(el: HTMLElement) {
    cleanup(el);
  },
};

export default noTabInto;
